import React from 'react';

import classNames from 'classnames';

import {
  TbCalendarEvent,
  TbClock,
  TbCoffee,
  TbPuzzle,
  TbPuzzle2,
  TbRepeat,
  TbCurrencyEuro,
  TbInfoSquare,
  TbInfoCircle,
} from 'react-icons/tb';

import { createLocale } from 'locales';

import Typography from 'components-new/typography/typography';

import {
  formatJobDurationDisplay,
  formatDateToLocaleNetherlands,
} from 'utils/display-text-formatter';

import type { OrganisationPeriodJobDetails } from 'state/__generated__/tadaahAPI';

import PairText from './PairText';

interface PeriodJobDetailsProps {
  periodJob: OrganisationPeriodJobDetails;
  className?: string;
  title?: string;
}

const localize = createLocale('organisationPeriodJobsLocales');

const PeriodJobDetails: React.FC<PeriodJobDetailsProps> = ({
  periodJob,
  className,
  title,
}) => {
  const boxShadow = '0px 2px 5px 0px rgba(25, 23, 26, 0.06)';

  const containerClassName = classNames(
    'px-6 sm:px-10 lg:px-16 border border-solid border-[#E7E6E8] grid gap-y-4 gap-x-2 grid-cols-2 md:grid-cols-4 lg:grid-cols-5 rounded-lg',
    className,
    !title && 'pt-3',
  );

  return (
    <div className={containerClassName} style={{ boxShadow }}>
      {title && (
        <div className="col-span-2 sm:col-span-4 lg:col-span-5">
          <Typography
            as="h2"
            variant="heading-2"
            className="mb-4 mt-3 text-gray-600"
          >
            {title}
          </Typography>
        </div>
      )}

      <PairText
        title={localize('startDate')}
        value={formatDateToLocaleNetherlands(periodJob.from_date)}
        icon={TbCalendarEvent}
      />
      <PairText
        title={localize('endDate')}
        value={formatDateToLocaleNetherlands(periodJob.to_date)}
        icon={TbCalendarEvent}
      />
      <PairText
        icon={TbClock}
        title={localize('time')}
        value={formatJobDurationDisplay(
          periodJob.start_time,
          periodJob.end_time,
          '',
        )}
      />
      <PairText
        icon={TbCoffee}
        title={localize('pause')}
        value={`${periodJob.pause ? periodJob.pause.toString() : '0'} minuten`}
      />
      <PairText
        icon={TbRepeat}
        title={localize('days')}
        value={periodJob.weekdays?.join(', ') || '?'}
      />
      <PairText
        banner
        title={localize('status')}
        icon={TbInfoSquare}
        value={classNames(
          periodJob.status === 'C' && localize('closed'),
          periodJob.status === 'O' && localize('open'),
        )}
      />
      <PairText
        title={localize('location')}
        value={periodJob.location || ''}
        icon="custLocation11"
      />
      <PairText
        title={localize('competences')}
        value={periodJob.competence || ''}
        icon={TbPuzzle}
      />
      <PairText
        icon={TbPuzzle2}
        title={localize('certificates')}
        value={classNames(
          periodJob.certificates?.length && periodJob.certificates?.join(', '),
          !periodJob.certificates?.length && localize('noCertificatesRequired'),
        )}
      />
      <PairText
        title={localize('maximalTarif')}
        icon={TbCurrencyEuro}
        value={`${`€${periodJob.max_rate}  (excl) / €${
          !!periodJob.max_rate && (periodJob.max_rate * 1.21).toFixed(2)
        }  (incl) / €${periodJob.max_rate_exempt} (vrijgesteld)`}`}
      />
      {!!periodJob.remarks?.length && (
        <PairText
          title={localize('remarks')}
          icon={TbInfoCircle}
          value={periodJob.remarks || ''}
          className="col-span-2 sm:col-span-4 lg:col-span-5 flex flex-col justify-start items-start mb-6"
        />
      )}
    </div>
  );
};

export default PeriodJobDetails;
